import { useEffect, useMemo, useState } from 'react';
import errorIcon from '@iconify/icons-ic/error-outline';
import successIcon from '@iconify/icons-ic/round-check-circle-outline';
import warningIcon from '@iconify/icons-ic/round-warning';
import { Icon, IconifyIcon } from '@iconify/react';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStores } from 'src/models';

/**
 * ## CAlert 설명
 * - 기본 Alert 창으로 경고 및 정보 등 여러 상황에 알맞은 Alert를 제공합니다.
 * - alert state를 통해 Alert 정보를 설정할 수 있습니다.
 */
const CAlert = () => {
  const theme = useTheme();
  const { alertStore } = useStores();
  const { open, category, title, content, hasCancelButton, btnLabel, closeAlert } = alertStore;

  // Alert 창의 카테고리별 theme 색상
  const categoryColors = useMemo(() => {
    return {
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      success: theme.palette.success.main,
    };
  }, []);

  // Alert 창의 카테고리별 아이콘 및 색상
  const [info, setInfo] = useState<{
    category: ButtonProps['color'];
    icon: IconifyIcon;
    color: string;
  }>({
    category: 'info',
    icon: errorIcon,
    color: categoryColors.info,
  });

  useEffect(() => {
    switch (category) {
      case 'success':
        setInfo({
          category: 'success',
          icon: successIcon,
          color: categoryColors.success,
        });
        break;
      case 'info':
        setInfo({
          category: 'info',
          icon: errorIcon,
          color: categoryColors.info,
        });
        break;
      case 'warning':
        setInfo({
          category: 'warning',
          icon: warningIcon,
          color: categoryColors.warning,
        });
        break;
      case 'error':
        setInfo({
          category: 'error',
          icon: errorIcon,
          color: categoryColors.error,
        });
        break;
    }
  }, [category]);

  // 모달 창 닫기
  const handleClose = () => {
    closeAlert();
  };

  // 확인 버튼 클릭 시
  const handleConfirm = () => {
    closeAlert();
    alertStore.invokeOnConfirm();
  };

  const handleCancel = () => {
    alertStore.invokeOnCancel(); // 취소 버튼 클릭 시 onCancel 실행
    handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
      <DialogTitle sx={{ pb: 2, display: 'flex', alignItems: 'center' }}>
        <Box
          component={Icon}
          icon={info.icon}
          sx={{ fontSize: '2rem', color: info.color, mr: 1 }}
        />
        {title}
      </DialogTitle>

      {content && (
        <DialogContent sx={{ typography: 'body2' }}>
          {content.split('<br/>').map((value, index) => (
            <Typography variant="body2" key={index}>
              {value}
              <br />
            </Typography>
          ))}
        </DialogContent>
      )}

      <DialogActions sx={{ justifyContent: 'right' }}>
        {hasCancelButton ? (
          <>
            <Button variant="outlined" color="inherit" onClick={handleCancel}>
              취소
            </Button>
            <Button
              variant="contained"
              color={info.category}
              onClick={handleConfirm}
              sx={{ color: '#fff' }}
            >
              {btnLabel || '확인'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            color={info.category}
            onClick={handleConfirm}
            sx={{ color: '#fff' }}
          >
            {btnLabel || '확인'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default observer(CAlert);
